/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";
import type {
  ContentConnectorType, ContentDestinationFormat, ContentDispatchStatus,
  ContentPublishingConnector, ContentPublishingDispatch,
} from "../types";
import { getFriendlyMessage, invokeConnectorManage } from "../_shared";
import { isEmailDispatch } from "./email";

export const checkSuppressedRecipients = async (
  workspaceId: string,
  recipients: string[],
): Promise<string[]> => {
  if (!recipients.length) return [];
  const data = await invokeConnectorManage(workspaceId, {
    action: "check_suppressed_recipients",
    recipients,
  });
  const suppressed = Array.isArray(data?.suppressed) ? data.suppressed : [];
  return suppressed.filter((v: unknown): v is string => typeof v === "string");
};

export type RunPublicationDispatchInput = {
  publicationId: string;
  connectorId: string;
  allowStale?: boolean;
  revisionNotes?: string;
  recipientOverride?: string[];
};

export const runPublicationDispatch = async (
  workspaceId: string,
  input: RunPublicationDispatchInput,
): Promise<ContentPublishingDispatch> => {
  const { data, error } = await supabase.functions.invoke("content-publication-dispatch", {
    body: {
      workspace_id: workspaceId,
      action: "dispatch",
      publication_id: input.publicationId,
      connector_id: input.connectorId,
      allow_stale: input.allowStale === true,
      revision_notes: input.revisionNotes ?? null,
      recipient_override: Array.isArray(input.recipientOverride) ? input.recipientOverride : undefined,
    },
  });
  if (error) {
    const message = (error as { message?: string }).message ?? "Dispatch failed";
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.dispatch) throw new Error("Dispatch did not return a result");
  return data.dispatch as ContentPublishingDispatch;
};

export const cancelPublicationDispatch = async (
  workspaceId: string,
  dispatchId: string,
): Promise<ContentPublishingDispatch> => {
  const { data, error } = await supabase.functions.invoke("content-publication-dispatch", {
    body: { workspace_id: workspaceId, action: "cancel", dispatch_id: dispatchId },
  });
  if (error) {
    const message = (error as { message?: string }).message ?? "Cancel failed";
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.dispatch) throw new Error("Cancel did not return a result");
  return data.dispatch as ContentPublishingDispatch;
};

export type DispatchHistoryFilters = {
  status?: "all" | ContentDispatchStatus;
  connectorType?: "all" | ContentConnectorType;
  search?: string;
  limit?: number;
  offset?: number;
};

export type DispatchHistoryRow = ContentPublishingDispatch & {
  connector: Pick<ContentPublishingConnector, "id" | "display_name" | "connector_type" | "status"> | null;
  publication: { id: string; piece_id: string; prepared_title: string | null; destination_format: ContentDestinationFormat } | null;
  brief: { id: string; title: string } | null;
};

export type DispatchHistoryResult = {
  rows: DispatchHistoryRow[];
  total: number;
};

/** Loads dispatch rows for a workspace with joined connector + publication +
 *  brief context, plus client-side filters. The total count reflects rows
 *  matching all filters before pagination. */
export const loadDispatchHistory = async (
  workspaceId: string,
  filters: DispatchHistoryFilters = {},
): Promise<DispatchHistoryResult> => {
  const limit = Math.max(1, Math.min(filters.limit ?? 50, 200));
  const offset = Math.max(0, filters.offset ?? 0);

  // Fetch a generous slice (most workspaces will be < few hundred rows). For
  // the first cut we filter client-side after enrichment.
  const { data, error } = await (supabase as any)
    .from("content_publishing_dispatches")
    .select(`
      *,
      connector:content_publishing_connectors(id, display_name, connector_type, status),
      publication:content_piece_publications(id, piece_id, prepared_title, destination_format)
    `)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw new Error(getFriendlyMessage(error, "Failed to load dispatch history"));

  const rawRows = (data ?? []) as Array<ContentPublishingDispatch & {
    connector: DispatchHistoryRow["connector"];
    publication: DispatchHistoryRow["publication"];
  }>;

  // Hydrate brief titles in a single follow-up query.
  const briefIds = Array.from(
    new Set(rawRows.map((r) => r.publication?.piece_id).filter((v): v is string => !!v)),
  );
  let briefMap = new Map<string, { id: string; title: string }>();
  if (briefIds.length) {
    const { data: briefs, error: briefsError } = await supabase
      .from("content_pieces")
      .select("id, title")
      .in("id", briefIds);
    if (briefsError) throw new Error(getFriendlyMessage(briefsError, "Failed to load brief titles"));
    briefMap = new Map((briefs ?? []).map((b) => [b.id as string, { id: b.id as string, title: b.title as string }]));
  }

  const enriched: DispatchHistoryRow[] = rawRows.map((row) => ({
    ...row,
    brief: row.publication?.piece_id ? briefMap.get(row.publication.piece_id) ?? null : null,
  }));

  const search = filters.search?.trim().toLowerCase() ?? "";
  const filtered = enriched.filter((row) => {
    if (filters.status && filters.status !== "all" && row.status !== filters.status) return false;
    if (filters.connectorType && filters.connectorType !== "all") {
      const ct = row.connector?.connector_type ?? (isEmailDispatch(row) ? "email" : null);
      if (ct !== filters.connectorType) return false;
    }
    if (search) {
      const haystack = [
        row.brief?.title,
        row.publication?.prepared_title,
        row.connector?.display_name,
        row.error_message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });

  return {
    rows: filtered.slice(offset, offset + limit),
    total: filtered.length,
  };
};

// ===== Suppression detail lookup (workspace-scoped) =====

export type SuppressionDetail = {
  email: string;
  reason: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export const getSuppressionDetails = async (
  workspaceId: string,
  emails: string[],
): Promise<SuppressionDetail[]> => {
  if (!emails.length) return [];
  const data = await invokeConnectorManage(workspaceId, {
    action: "get_suppression_details",
    emails,
  });
  const raw = Array.isArray(data?.suppressions) ? data.suppressions : [];
  return raw
    .filter((r: unknown): r is Record<string, unknown> => !!r && typeof r === "object")
    .map((r: Record<string, unknown>) => ({
      email: typeof r.email === "string" ? r.email.toLowerCase() : "",
      reason: typeof r.reason === "string" ? r.reason : "unknown",
      metadata:
        r.metadata && typeof r.metadata === "object" && !Array.isArray(r.metadata)
          ? (r.metadata as Record<string, unknown>)
          : null,
      created_at: typeof r.created_at === "string" ? r.created_at : "",
    }))
    .filter((r: SuppressionDetail) => r.email);
};
